import React, {useState} from 'react';
import {
  StyleSheet,
  TextInput,
  Pressable,
  ActivityIndicator,
  Linking,
  Alert,
  Modal,
  ScrollView,
} from 'react-native';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';
import {formatCurrency} from '@/Utils/CommonUtils';
import {MaterialCommunityIcons} from '@expo/vector-icons';
import dayjs from 'dayjs';
import useValidateCoupon from '@/Hooks/RenewalApi/useValidateCoupon';
import useRequestCallback from '@/Hooks/RenewalApi/useRequestCallback';
import {RenewalResponse} from '@/Hooks/RenewalApi/useGetRenewalForUser';
import Tooltip from '@/Atoms/Tooltip';
import useAlertModalStore from '@/Store/AlertModalStore';
import ViewBreakupModal from './ViewBreakupModal';
import Spacer from '@/Atoms/Spacer';

interface SelectedSlot {
  date: string;
  time: string;
}

interface OrderSummaryProps {
  baseFee: number;
  maxBasePrice?: number; // Max price (from max_price_per_session)
  offerPrice?: number; // Offer price (same as baseFee, for clarity)
  offerDiscountAmount?: number; // Discount amount from max to offer
  offerDiscountPercentage?: number; // Discount percentage from max to offer
  coachPremium?: number;
  discount: number;
  discountPct?: number;  // Duration discount percentage
  couponDiscount?: number;
  gst: number;
  total: number;
  currency: string;
  onPay: () => void;
  isMobile: boolean;
  selectedPlanType?: 'personal' | 'group' | 'webinar';
  selectedDuration?: number;
  selectedSlots?: SelectedSlot[];
  selectedBatch?: {
    batch_name: string;
    batch_code: string;
    coach_name: string;
    class_timing: string;
    class_days: string;
    start_date: string;
    tournament_id: number;
    level_name: string | null;
  } | null;
  onRemoveSlot?: (index: number) => void;
  onViewCoupons?: () => void;
  onRemoveCoupon?: () => void;
  onApplyCoupon?: (coupon: any) => void;
  appliedCoupon?: any;
  tournamentId?: number;
  hidePayButton?: boolean;
  selectedPricing?: {
    level_name?: string | null;
    level_slug?: string | null;
    format_name?: string | null;
    format_slug?: string | null;
    coach_tier_code?: string | null;
    max_price_per_session?: string | null;
    roadmaps?: Array<{
      duration_months: number;
      total_price: string;
      monthly_equivalent: string;
      incomplete: boolean;
      remaining_months: number;
      segments: Array<{
        pricing_id: number;
        level_slug: string | null;
        level_name: string | null;
        format_slug: string | null;
        format_name: string | null;
        coach_tier_code: string | null;
        coach_tier_name: string | null;
        tournament_id: number | null;
        months_used: number;
        price_per_session: string;
        segment_total: string;
        currency_code: string;
      }>;
    }>;
    currency?: string;
    currency_code?: string;
  } | null;
  selectedCoach?: string; // coach tier code
  renewalData?: RenewalResponse; // for user info and current subscription
  onRequestCallback?: () => void; // optional callback
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  baseFee,
  maxBasePrice,
  offerPrice,
  offerDiscountAmount,
  offerDiscountPercentage,
  coachPremium = 0,
  discount,
  discountPct = 0,
  couponDiscount = 0,
  gst,
  total,
  currency,
  onPay,
  isMobile,
  selectedPlanType,
  selectedDuration,
  selectedSlots = [],
  selectedBatch = null,
  onRemoveSlot,
  onViewCoupons,
  onRemoveCoupon,
  onApplyCoupon,
  appliedCoupon,
  tournamentId,
  hidePayButton = false,
  selectedPricing,
  selectedCoach,
  renewalData,
  onRequestCallback,
}) => {
  const getPlanTypeLabel = () => {
    switch (selectedPlanType) {
      case 'personal':
        return 'Personal 1:1';
      case 'group':
        return 'Small Group';
      case 'webinar':
        return 'Masterclass';
      default:
        return 'Plan';
    }
  };
  const openAlertModal = useAlertModalStore(state => state.openModal);

  const [couponCode, setCouponCode] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [callbackRequested, setCallbackRequested] = useState(false);
  const [callbackRequestId, setCallbackRequestId] = useState<number | null>(null);

  const {mutate: validateCoupon, isLoading: isCheckingCoupon} =
    useValidateCoupon(
      data => {
        if (data.valid && data.coupon) {
          setValidationError(null);
          if (onApplyCoupon) {
            onApplyCoupon(data.coupon);
          }
          setCouponCode('');
        } else {
          setValidationError(data.error || 'Invalid coupon code');
        }
      },
      (error: any) => {
        setValidationError(
          error?.response?.data?.error ||
            error?.message ||
            'Failed to validate coupon',
        );
      },
    );

  // Map selectedCoach to coach tier code
  const coachTierCodeMap: Record<string, string> = {
    'general': 'GEN',
    'fide_master': 'FM',
    'international_master': 'IM',
    'grand_master': 'GM',
  };
  const selectedCoachTierCode = selectedCoach ? (coachTierCodeMap[selectedCoach] || 'GEN') : (selectedPricing?.coach_tier_code || 'GEN');

  const {mutate: requestCallback, isLoading: isRequestingCallback} = useRequestCallback(
    data => {
      if (data.success) {
        setCallbackRequested(true);
        if (data.request_id) {
          setCallbackRequestId(data.request_id);
        }
        openAlertModal({
          header: 'Callback Requested',
          bodyTxt: 'Your callback request has been submitted. Our team will contact you shortly.',
          buttonText: 'OK',
        });
        // openAlertModal({
        //   'Callback Requested',
        //   bodyTxt: 'Your callback request has been submitted. Our team will contact you shortly.',
        //   buttonText: 'OK',
        // });
        if (onRequestCallback) {
          onRequestCallback();
        }
      } else {
        Alert.alert('Error', data.message || 'Failed to submit callback request. Please try again.');
      }
    },
    (error: any) => {
      Alert.alert('Error', 'Failed to submit callback request. Please try again.');
      console.error('Callback request error:', error);
    },
  );

  const handleRequestCallback = () => {
    if (!selectedPlanType || !selectedDuration) {
      Alert.alert('Error', 'Please select a plan type and duration before requesting a callback.');
      return;
    }

    requestCallback({
      selectedPlanType,
      selectedDuration,
      selectedCoach: selectedCoachTierCode,
      selectedSlots: selectedSlots.length > 0 ? selectedSlots : undefined,
      tournamentId,
      selectedPricing: selectedPricing ? {
        level_slug: selectedPricing.level_slug,
        level_name: selectedPricing.level_name,
        format_slug: selectedPricing.format_slug,
        format_name: selectedPricing.format_name,
        coach_tier_code: selectedPricing.coach_tier_code,
      } : undefined,
    });
  };

  const handleCheckCoupon = () => {
    if (!couponCode.trim()) {
      setValidationError('Please enter a coupon code');
      return;
    }
    if (!tournamentId || !selectedPlanType || !selectedDuration) {
      setValidationError('Please ensure plan details are selected');
      return;
    }
    setValidationError(null);
    validateCoupon({
      coupon_code: couponCode.trim(),
      tournament_id: tournamentId,
      plan_type: selectedPlanType,
      duration_months: selectedDuration,
    });
  };

  return (
    <Box
      backgroundColor={CCColors.White}
      padding={20}
      borderRadius={12}
      style={styles.cardShadow}
      width={isMobile ? '100%' : 350}>
      {/* Selection Summary */}
      {(selectedPlanType || selectedDuration || selectedSlots.length > 0) && (
        <Box marginBottom={30}>
          <CCText
            fontSize={FontSizes[16]}
            fontWeight="bold"
            color={CCColors.TextColor.Dark}
            marginBottom={15}>
            Your Selection
          </CCText>

          {/* Level */}
          {selectedPricing?.level_name && (
            <Box
              flexDirection="row"
              justifyContent="space-between"
              marginBottom={10}>
              <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Grey}>
                Level
              </CCText>
              <CCText
                fontSize={FontSizes[14]}
                fontWeight="semibold"
                color={CCColors.TextColor.Dark}>
                {selectedPricing.level_name}
              </CCText>
            </Box>
          )}

          {/* Plan Type */}
          {selectedPlanType && (
            <Box
              flexDirection="row"
              justifyContent="space-between"
              marginBottom={10}>
              <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Grey}>
                Plan Type
              </CCText>
              <CCText
                fontSize={FontSizes[14]}
                fontWeight="semibold"
                color={CCColors.TextColor.Dark}>
                {getPlanTypeLabel()}
              </CCText>
            </Box>
          )}

          {/* Duration */}
          {selectedDuration && (
            <Box
              flexDirection="row"
              justifyContent="space-between"
              marginBottom={10}>
              <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Grey}>
                Duration
              </CCText>
              <CCText
                fontSize={FontSizes[14]}
                fontWeight="semibold"
                color={CCColors.TextColor.Dark}>
                {selectedDuration} Months
              </CCText>
            </Box>
          )}

          {/* Selected Batch (for group plan type) - Only show on mobile */}
          {selectedPlanType === 'group' && selectedBatch && isMobile && (
            <Box marginTop={10}>
              <CCText
                fontSize={FontSizes[14]}
                color={CCColors.TextColor.Grey}
                marginBottom={8}>
                Selected Batch
              </CCText>
              <Box
                padding={14}
                backgroundColor={CCColors.Primary.lightBrown}
                borderRadius={10}
                borderWidth={2}
                borderColor={CCColors.Primary.Default}
                style={styles.batchCard}>
                {/* Compact 2-line display: Line 1 - Batch Name + Coach */}
                <Box flexDirection="row" flexWrap="wrap" alignItems="center" gap={8} marginBottom={6}>
                  <CCText
                    fontSize={FontSizes[13]}
                    fontWeight="semibold"
                    color={CCColors.TextColor.Dark}
                    numberOfLines={1}
                    style={{flex: 1, minWidth: 120}}>
                    {selectedBatch.batch_name}
                  </CCText>
                  {selectedBatch.coach_name && (
                    <Box flexDirection="row" alignItems="center" gap={4}>
                      <MaterialCommunityIcons name="account" size={12} color={CCColors.TextColor.Grey} />
                      <CCText fontSize={FontSizes[11]} color={CCColors.TextColor.Grey} numberOfLines={1}>
                        {selectedBatch.coach_name}
                      </CCText>
                    </Box>
                  )}
                </Box>
                {/* Line 2 - Days + Time */}
                <Box flexDirection="row" flexWrap="wrap" alignItems="center" gap={8}>
                  {selectedBatch.class_days && (
                    <Box flexDirection="row" alignItems="center" gap={4}>
                      <MaterialCommunityIcons name="calendar-week" size={12} color={CCColors.TextColor.Grey} />
                      <CCText fontSize={FontSizes[11]} color={CCColors.TextColor.Grey} numberOfLines={1}>
                        {selectedBatch.class_days.split(',').map(d => {
                          const dayMap: {[key: string]: string} = {
                            '0': 'Sun', '1': 'Mon', '2': 'Tue', '3': 'Wed',
                            '4': 'Thu', '5': 'Fri', '6': 'Sat'
                          };
                          return dayMap[d.trim()] || d.trim();
                        }).join(', ')}
                      </CCText>
                    </Box>
                  )}
                  {selectedBatch.class_timing && (
                    <Box flexDirection="row" alignItems="center" gap={4}>
                      <MaterialCommunityIcons name="clock-outline" size={12} color={CCColors.TextColor.Grey} />
                      <CCText fontSize={FontSizes[11]} color={CCColors.TextColor.Grey} numberOfLines={1}>
                        {selectedBatch.class_timing.split(',').slice(0, 2).join(', ')}
                        {selectedBatch.class_timing.split(',').length > 2 ? '...' : ''}
                      </CCText>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          {/* Custom Slots for Group Plan Type */}
          {selectedPlanType === 'group' && selectedSlots.length > 0 && (
            <Box marginTop={10}>
              <CCText
                fontSize={FontSizes[14]}
                color={CCColors.TextColor.Grey}
                marginBottom={8}>
                Custom Time Slots ({selectedSlots.length})
              </CCText>
              <Box
                padding={14}
                backgroundColor={CCColors.Grey.Divider200}
                borderRadius={10}
                borderWidth={1}
                borderColor={CCColors.BorderBottom}>
              <Box flexDirection="row" flexWrap="wrap" gap={6}>
                {selectedSlots.map((slot, index) => {
                  const slotDate = dayjs(slot.date);
                  const dayName = slotDate.format('ddd');
                    const dateStr = slotDate.format('MMM D');
                  return (
                      <Box
                      key={`${slot.date}_${slot.time}_${index}`}
                        flexDirection="row"
                        alignItems="center"
                        gap={6}
                        backgroundColor={CCColors.White}
                        paddingHorizontal={10}
                        paddingVertical={6}
                        borderRadius={6}
                        borderWidth={1}
                        borderColor={CCColors.BorderBottom}>
                        <MaterialCommunityIcons
                          name="calendar-clock"
                          size={14}
                          color={CCColors.Primary.Default}
                        />
                      <CCText
                          fontSize={FontSizes[11]}
                        color={CCColors.TextColor.Dark}
                          fontWeight="semibold">
                          {dayName} {dateStr}
                        </CCText>
                        <CCText
                          fontSize={FontSizes[11]}
                          color={CCColors.TextColor.Dark}>
                        {(() => {
                          try {
                            const timeStr = slot.time.includes('T')
                              ? dayjs(slot.time).format('h:mm A')
                                : dayjs(`2000-01-01 ${slot.time}`).format('h:mm A');
                            return timeStr;
                          } catch {
                            return slot.time;
                          }
                        })()}
                        </CCText>
                      </Box>
                    );
                  })}
                </Box>
                <Box marginTop={8} padding={8} backgroundColor={CCColors.Primary.lightYellow} borderRadius={6}>
                  <CCText fontSize={FontSizes[11]} color={CCColors.TextColor.Dark} lineHeight={16}>
                    <MaterialCommunityIcons name="information" size={12} color={CCColors.Primary.Dark} />{' '}
                    Our team will contact you to find the best batch matching your preferences.
                  </CCText>
                </Box>
              </Box>
            </Box>
          )}

          {/* Selected Slots (for personal/webinar plan types) */}
          {selectedSlots.length > 0 && selectedPlanType !== 'group' && (
            <Box marginTop={10}>
              <CCText
                fontSize={FontSizes[14]}
                color={CCColors.TextColor.Grey}
                marginBottom={8}>
                Time Slots ({selectedSlots.length})
              </CCText>
              <Box flexDirection="row" flexWrap="wrap" gap={6}>
                {selectedSlots.map((slot, index) => {
                  const slotDate = dayjs(slot.date);
                  const dayName = slotDate.format('ddd');
                  const timeStr = (() => {
                    // Parse time - could be HH:mm:ss or full datetime
                    try {
                      return slot.time.includes('T')
                        ? dayjs(slot.time).format('h:mm A')
                        : dayjs(`2000-01-01 ${slot.time}`).format('h:mm A');
                    } catch {
                      return slot.time;
                    }
                  })();
                  return (
                    <Pressable
                      key={`${slot.date}_${slot.time}_${index}`}
                      onPress={() => onRemoveSlot && onRemoveSlot(index)}
                      style={styles.slotTag}>
                      <CCText
                        fontSize={FontSizes[10]}
                        color={CCColors.TextColor.Dark}
                        fontWeight="semibold"
                        numberOfLines={1}
                        style={{maxWidth: 75, flexShrink: 1}}>
                        {dayName} {timeStr}
                      </CCText>
                      <MaterialCommunityIcons
                        name="close-circle"
                        size={12}
                        color={CCColors.TextColor.Grey}
                      />
                    </Pressable>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Coupon Code */}
      <Box marginBottom={30}>
        <Box
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          marginBottom={8}>
          <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Dark}>
            Coupon Code
          </CCText>
          {onViewCoupons && (
            <Pressable onPress={onViewCoupons}>
              <Box flexDirection="row" alignItems="center" gap={4}>
                <MaterialCommunityIcons
                  name="ticket-percent"
                  size={14}
                  color={CCColors.Primary.Default}
                />
                <CCText
                  fontSize={FontSizes[12]}
                  color={CCColors.Primary.Default}
                  fontWeight="semibold">
                  View
                </CCText>
              </Box>
            </Pressable>
          )}
        </Box>
        {appliedCoupon ? (
          <Box
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            padding={12}
            backgroundColor={CCColors.Primary.lightBrown}
            borderRadius={8}
            borderWidth={1}
            borderColor={CCColors.Green}>
            <Box flexDirection="row" alignItems="center" gap={8}>
              <MaterialCommunityIcons
                name="check-circle"
                size={18}
                color={CCColors.Green}
              />
              <CCText
                fontSize={FontSizes[14]}
                fontWeight="semibold"
                color={CCColors.TextColor.Dark}>
                {appliedCoupon.code}
              </CCText>
            </Box>
            {onRemoveCoupon && (
              <Pressable 
                onPress={onRemoveCoupon}
                hitSlop={{top: 10, bottom: 10, left: 10, right: 10}}
                style={{padding: 4}}>
                <MaterialCommunityIcons
                  name="close-circle"
                  size={20}
                  color={CCColors.Red}
                />
              </Pressable>
            )}
          </Box>
        ) : (
          <Box
            flexDirection="row"
            alignItems="center"
            borderWidth={1}
            borderColor={CCColors.Grey.Divider200}
            borderRadius={8}
            paddingHorizontal={10}
            height={48}>
            <TextInput
              style={styles.input}
              placeholder="Enter code"
              placeholderTextColor={CCColors.TextColor.Grey}
              value={couponCode}
              onChangeText={text => {
                setCouponCode(text);
                setValidationError(null);
              }}
            />
            <Pressable
              style={[
                styles.checkButton,
                (isCheckingCoupon || !couponCode.trim()) &&
                  styles.checkButtonDisabled,
              ]}
              onPress={handleCheckCoupon}
              disabled={isCheckingCoupon || !couponCode.trim()}>
              <Box
                flexDirection="row"
                alignItems="center"
                justifyContent="center"
                gap={6}>
                {isCheckingCoupon && (
                  <ActivityIndicator
                    size="small"
                    color={CCColors.TextColor.Dark}
                  />
                )}
                <CCText
                  fontSize={FontSizes[12]}
                  color={
                    isCheckingCoupon
                      ? CCColors.TextColor.Grey
                      : CCColors.TextColor.Dark
                  }
                  fontWeight="bold">
                  {isCheckingCoupon ? 'Checking...' : 'Check'}
                </CCText>
              </Box>
            </Pressable>
          </Box>
        )}
        {validationError && (
          <CCText fontSize={FontSizes[12]} color={CCColors.Red} marginTop={5}>
            {validationError}
          </CCText>
        )}
      </Box>

      {/* Bill Details */}
      <Box marginBottom={30}>
        <CCText
          fontSize={FontSizes[16]}
          fontWeight="bold"
          color={CCColors.TextColor.Dark}
          marginBottom={20}>
          Bill Details
        </CCText>

        {/* Applied Coupon Display */}
        {appliedCoupon && (
          <Box
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            padding={8}
            backgroundColor={CCColors.Primary.lightBrown}
            borderRadius={6}
            marginBottom={12}>
            <Box flexDirection="row" alignItems="center" gap={6}>
              <MaterialCommunityIcons
                name="ticket-percent"
                size={14}
                color={CCColors.Primary.Default}
              />
              <CCText
                fontSize={FontSizes[12]}
                color={CCColors.TextColor.Dark}
                fontWeight="semibold">
                {appliedCoupon.code}
              </CCText>
              <CCText fontSize={FontSizes[11]} color={CCColors.TextColor.Grey}>
                (
                {appliedCoupon.discount_type === 'percentage'
                  ? `${appliedCoupon.discount_value}% off`
                  : `${formatCurrency(
                      parseFloat(appliedCoupon.discount_value),
                      currency,
                    )} off`}
                )
              </CCText>
            </Box>
            {onRemoveCoupon && (
              <Pressable 
                onPress={onRemoveCoupon}
                hitSlop={{top: 10, bottom: 10, left: 10, right: 10}}
                style={{padding: 4}}>
                <MaterialCommunityIcons
                  name="close-circle"
                  size={18}
                  color={CCColors.Red}
                />
              </Pressable>
            )}
          </Box>
        )}

        <Box
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          marginBottom={15}>
          <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Dark}>
            Base Fee{currency === 'INR' ? ' (incl. GST)' : ''}
          </CCText>
          <Box alignItems="flex-end">
            <Box flexDirection="row" alignItems="center" gap={8}>
              <CCText
                fontSize={FontSizes[14]}
                color={CCColors.TextColor.Dark}
                fontWeight="semibold">
                {formatCurrency(maxBasePrice || baseFee, currency)}
              </CCText>
              {offerDiscountPercentage && offerDiscountPercentage > 0 ? (
                <Box
                  backgroundColor={CCColors.Orange}
                  paddingHorizontal={6}
                  paddingVertical={2}
                  borderRadius={4}>
                  <CCText
                    fontSize={FontSizes[10]}
                    color={CCColors.White}
                    fontWeight="bold">
                    OFFER
                  </CCText>
                </Box>
              ):<></>}
            </Box>
          </Box>
        </Box>

        {discount > 0 && (
          <Box
            flexDirection="row"
            justifyContent="space-between"
            marginBottom={15}>
            <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Dark}>
              Duration discount{discountPct > 0 ? ` (${discountPct}% off)` : ''}
            </CCText>
            <CCText
              fontSize={FontSizes[14]}
              color={CCColors.Green}
              fontWeight="bold">
              -{formatCurrency(discount, currency)}
            </CCText>
          </Box>
        )}

        {couponDiscount > 0 && (
          <Box
            flexDirection="row"
            justifyContent="space-between"
            marginBottom={15}>
            <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Dark}>
              Coupon discount
            </CCText>
            <CCText
              fontSize={FontSizes[14]}
              color={CCColors.Green}
              fontWeight="bold">
              -{formatCurrency(couponDiscount, currency)}
            </CCText>
          </Box>
        )}

        {/* GST - Only show for INR */}
        {currency === 'INR' && gst > 0 && (
          <Box
            flexDirection="row"
            justifyContent="space-between"
            marginBottom={15}>
            <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Dark}>
              GST (18%){' '}
              <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.Grey}>
                (included)
              </CCText>
            </CCText>
            <CCText
              fontSize={FontSizes[14]}
              color={CCColors.TextColor.Dark}
              fontWeight="bold">
              {formatCurrency(gst, currency)}
            </CCText>
          </Box>
        )}

        <Box
          height={1}
          backgroundColor={CCColors.Grey.Divider200}
          marginBottom={15}
        />

        {/* View Breakup Button */}
        {selectedPricing && selectedDuration && (() => {
          const roadmap = selectedPricing.roadmaps?.find(
            r => r.duration_months === selectedDuration
          );
          return roadmap && roadmap.segments && roadmap.segments.length > 0 ? (
            <Box marginBottom={16} alignItems="flex-end">
              <ViewBreakupModal
                segments={roadmap.segments}
                totalPrice={roadmap.total_price}
                currency={selectedPricing.currency_code || selectedPricing.currency || 'INR'}
                title={`${selectedDuration} Months Roadmap`}
                buttonLabel="View Breakup"
              />
            </Box>
          ) : null;
        })()}

        <Box
          flexDirection="row"
          justifyContent="space-between"
          alignItems="flex-start"
          marginBottom={20}>
          <CCText
            fontSize={FontSizes[16]}
            fontWeight="bold"
            color={CCColors.TextColor.Dark}>
            Grand Total
          </CCText>
          <Box alignItems="flex-end">
            <CCText
              fontSize={FontSizes[18]}
              fontWeight="bold"
              color={CCColors.TextColor.Dark}>
              {formatCurrency(total, currency)}
            </CCText>
            <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.Grey}>
              (
              {formatCurrency(
                Math.round(total / (selectedDuration || 12)),
                currency,
              )}{' '}
              Monthly)
            </CCText>
          </Box>
        </Box>

        {/* Pay Button */}
        <Box style={{flexDirection:'row',justifyContent:'space-between' , alignItems:'center',width:'100%'}}>
        {!hidePayButton && (
          <CCButton
            onPress={onPay}
            disabled={total <= 0}
              style={[styles.payButton]}
              parentStyle={{flex:1}}
            contentContainerStyle={[styles.payButtonContent]}>
            <CCText
              fontSize={FontSizes[16]}
              fontWeight="bold"
              textAlign='center'
              color={CCColors.TextColor.Dark}>
              Pay {formatCurrency(total, currency)}
            </CCText>
          </CCButton>
        )}
        
        </Box>
        <Spacer spacing={10} />
        {!callbackRequested ? (
              <Pressable
              onPress={handleRequestCallback}
              disabled={isRequestingCallback}
              style={[
                styles.callbackButton,
                isRequestingCallback && styles.callbackButtonDisabled
              ]}
              >
              {isRequestingCallback ? (
                <Box flexDirection="row" alignItems="center" justifyContent="center" gap={8}>
                  <ActivityIndicator size="small" color={CCColors.Primary.Default} />
              <CCText
                fontSize={FontSizes[16]}
                fontWeight="bold"
                textAlign='center'
                color={CCColors.Primary.Default}>
                    Requesting...
              </CCText>
            </Box>
              ) : (
                <CCText
                  fontSize={FontSizes[16]}
                  fontWeight="bold"
                  textAlign='center'
                  color={CCColors.Primary.Default}>
                  Request callback ?
                </CCText>
              )}
            </Pressable>
            
          ) : (
            <Box
                disabled={isRequestingCallback}
                >
                  <CCText
                fontSize={FontSizes[16]}
                fontWeight="bold"
                textAlign='center'
                color={CCColors.Primary.Default}>
                Requested Successfully
              </CCText>
              </Box>
          )}
        <Box flexDirection="row" gap={12} alignItems="center">
          {/* Request Callback Button */}
          

          {/* Direct Call Button */}
          {/* <Pressable
            style={styles.callButton}
            onPress={() => {
              const phoneNumber = '09620006600'; // Remove spaces for tel: protocol
              Linking.openURL(`tel:${phoneNumber}`).catch(err => {
                console.error('Error opening phone dialer:', err);
                // Fallback: try with +91 prefix
                Linking.openURL(`tel:+91${phoneNumber}`).catch(err2 => {
                  console.error('Error with +91 prefix:', err2);
                });
              });
            }}>
            <MaterialCommunityIcons
              name="phone"
              size={24}
              color={CCColors.White}
            />
          </Pressable> */}
        </Box>
      </Box>

    </Box>
  );
};

const styles = StyleSheet.create({
  cardShadow: {
    shadowColor: '#4D3F37',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 3,
  },
  callButton: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: CCColors.Primary.Default,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
    zIndex: 1000,
  },
  callButtonDisabled: {
    opacity: 0.6,
  },
  callbackRequestedBadge: {
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 4,
  },
  input: {
    flex: 1,
    height: '100%',
    fontSize: 14,
    color: CCColors.TextColor.Dark,
  },
  checkButton: {
    backgroundColor: CCColors.Primary.Default,
    paddingHorizontal: 15,
    paddingVertical: 6,
    borderRadius: 15,
    minWidth: 60,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkButtonDisabled: {
    opacity: 0.5,
  },
  payButton: {
    backgroundColor: CCColors.Primary.Default,
    borderRadius: 8,
    paddingVertical: 5,
    width: '100%',
  },
  payButtonContent: {
    padding: 0,
  },
  slotTag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 5,
    backgroundColor: CCColors.Primary.lightBrown,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: CCColors.Primary.Default,
    maxWidth: 100,
  },
  batchCard: {
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  callbackButton: {
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  callbackButtonDisabled: {
    opacity: 0.6,
  },
});

export default OrderSummary;
